<template>
  <v-card variant="outlined" class="pa-4 workshop-task">
    <div class="text-caption text-medium-emphasis">
      {{ $t(groupKey(task.group)) }} ·
      {{
        $t("workshop.checklist.position", {
          position: task.position,
          total: taskCount,
        })
      }}
    </div>
    <div class="d-flex flex-wrap align-center ga-2 mt-1 mb-3">
      <span class="text-h6 mr-auto workshop-wrap">
        {{ $t(taskKey(task.code)) }}
      </span>
      <v-chip v-if="task.mandatory" size="small" variant="outlined">
        {{ $t("workshop.checklist.mandatory") }}
      </v-chip>
      <v-chip size="small" variant="tonal" :color="status.color">
        {{ $t(status.key) }}
      </v-chip>
    </div>

    <!-- Order hint (M1): advisory only, nothing is disabled. -->
    <v-alert
      v-if="hint"
      type="info"
      variant="tonal"
      density="compact"
      class="mb-3"
    >
      <div class="d-flex flex-wrap align-center">
        <span class="mr-auto py-1">{{ $t(hint.key) }}</span>
        <v-btn
          size="small"
          variant="text"
          color="primary"
          @click="$emit('select', hint.target)"
        >
          {{ $t("workshop.checklist.go") }}
        </v-btn>
      </div>
      <div class="text-caption">
        {{ $t("workshop.checklist.hint_any_order") }}
      </div>
    </v-alert>

    <!-- Result form (slice 5): while the task has no current result, or as a redo after "Ripeti"
         (slice 7), and the checklist is not approved. The input stays after an error. -->
    <div v-if="showForm">
      <div v-if="redoOf" class="d-flex flex-wrap align-center ga-2 mb-3">
        <span class="text-body-2 text-medium-emphasis mr-auto">
          {{ $t("workshop.form.redo_note") }}
        </span>
        <v-btn
          variant="text"
          color="primary"
          :disabled="saving"
          @click="resetForm"
        >
          {{ $t("workshop.form.cancel") }}
        </v-btn>
      </div>
      <div class="text-body-2 font-weight-medium mb-2">
        {{ $t("workshop.form.result") }}
      </div>
      <div class="workshop-result-grid">
        <v-btn
          v-for="button in resultButtons"
          :key="button.result"
          :variant="form.result === button.result ? 'flat' : 'outlined'"
          :color="button.color"
          :disabled="button.disabled || saving"
          class="workshop-result-btn"
          @click="pickResult(button.result)"
        >
          <span class="text-wrap">{{ $t(button.key) }}</span>
        </v-btn>
      </div>
      <div v-if="errorText('result')" class="text-caption text-error mt-1">
        {{ errorText("result") }}
      </div>

      <template v-if="form.result">
        <div v-if="fields.quantity" class="d-flex flex-wrap ga-3 mt-4">
          <v-text-field
            v-model="form.quantityValue"
            class="workshop-quantity"
            :label="
              $t(
                fields.quantityRequired
                  ? 'workshop.form.quantity'
                  : 'workshop.form.quantity_optional',
              )
            "
            inputmode="decimal"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            :error-messages="errorText('quantityValue')"
          />
          <div>
            <v-btn-toggle
              v-model="form.quantityUnit"
              mandatory
              divided
              variant="outlined"
              color="primary"
              class="workshop-unit"
            >
              <v-btn
                v-for="unit in fields.units"
                :key="unit"
                :value="unit"
                min-width="56"
              >
                {{ unit }}
              </v-btn>
            </v-btn-toggle>
            <div
              v-if="errorText('quantityUnit')"
              class="text-caption text-error mt-1"
            >
              {{ errorText("quantityUnit") }}
            </div>
          </div>
          <v-select
            v-model="form.quantityBasis"
            class="workshop-basis"
            :items="basisItems"
            :label="$t('workshop.form.basis')"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            :error-messages="errorText('quantityBasis')"
          />
        </div>

        <container-picker
          v-if="fields.containerPicker"
          v-model="form.containerId"
          class="mt-4"
          :task-code="task.code"
          :unit="form.quantityUnit"
          :required="fields.containerPickerRequired"
          :label="
            fields.containerPickerRequired
              ? 'workshop.form.container_picker'
              : 'workshop.form.container_picker_optional'
          "
          :error-messages="errorText('containerId')"
          @picked="pickedContainer = $event"
        />

        <v-text-field
          v-if="fields.container"
          v-model="form.container"
          class="mt-4"
          :label="$t('workshop.form.container')"
          :placeholder="$t('workshop.form.container_hint')"
          :maxlength="containerMax"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
          :error-messages="errorText('container')"
        />

        <div class="mt-4">
          <div v-if="quickReasons.length" class="d-flex flex-wrap ga-2 mb-2">
            <v-chip
              v-for="key in quickReasons"
              :key="key"
              size="small"
              variant="outlined"
              color="primary"
              prepend-icon="mdi-lightning-bolt-outline"
              @click="form.reason = $t(key)"
            >
              {{ $t(key) }}
            </v-chip>
          </div>
          <v-textarea
            v-model="form.reason"
            :label="
              $t(
                fields.reasonRequired
                  ? 'workshop.form.reason'
                  : 'workshop.form.reason_optional',
              )
            "
            :maxlength="reasonMax"
            rows="2"
            auto-grow
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            :error-messages="errorText('reason')"
          />
        </div>

        <v-text-field
          v-model="form.performedAt"
          class="mt-4"
          type="datetime-local"
          :label="$t('workshop.form.performed_at')"
          :min="limits.min"
          :max="limits.max"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
          :error-messages="errorText('performedAt')"
        />

        <!-- Evidence files (slice 6): camera or file picker, kept until "Salva" or removed. -->
        <div class="mt-4">
          <div class="text-body-2 font-weight-medium mb-2">
            {{
              $t(
                evidenceNeeded
                  ? "workshop.form.evidence"
                  : "workshop.form.evidence_optional",
              )
            }}
          </div>
          <div class="d-flex flex-wrap ga-2">
            <v-btn
              variant="outlined"
              color="primary"
              prepend-icon="mdi-camera-outline"
              class="workshop-file-btn"
              :disabled="saving"
              @click="takePhoto"
            >
              {{ $t("workshop.form.take_photo") }}
            </v-btn>
            <v-btn
              variant="outlined"
              color="primary"
              prepend-icon="mdi-paperclip"
              class="workshop-file-btn"
              :disabled="saving"
              @click="$refs.picker.click()"
            >
              {{ $t("workshop.form.attach_file") }}
            </v-btn>
          </div>
          <input
            ref="camera"
            type="file"
            accept="image/*"
            capture="environment"
            class="d-none"
            @change="addFiles"
          />
          <input
            ref="picker"
            type="file"
            :accept="fileAccept"
            multiple
            class="d-none"
            @change="addFiles"
          />
          <div
            class="text-caption mt-1"
            :class="filesBroken ? 'text-error' : 'text-medium-emphasis'"
          >
            {{ $t("workshop.form.files_limit") }}
          </div>
          <div v-if="previews.length" class="workshop-thumbs mt-2">
            <div
              v-for="(item, index) in previews"
              :key="item.key"
              class="workshop-thumb"
              :title="item.name"
            >
              <img v-if="item.url" :src="item.url" :alt="item.name" />
              <v-icon v-else color="grey">mdi-file-pdf-box</v-icon>
              <span class="workshop-thumb-name text-caption">
                {{ item.name }}
              </span>
              <v-btn
                class="workshop-thumb-remove"
                icon="mdi-close"
                size="x-small"
                variant="flat"
                :disabled="saving"
                :title="$t('workshop.form.remove_file')"
                :aria-label="$t('workshop.form.remove_file')"
                @click="removeFile(index)"
              />
            </div>
          </div>
          <div v-if="errorText('files')" class="text-caption text-error mt-1">
            {{ errorText("files") }}
          </div>
        </div>

        <v-alert
          v-if="generalError"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          {{ generalError }}
        </v-alert>

        <v-btn
          block
          size="large"
          color="primary"
          variant="flat"
          class="mt-4 workshop-save"
          :loading="saving"
          :disabled="saving || filesBroken"
          @click="save"
        >
          {{ $t("workshop.form.save") }}
        </v-btn>
      </template>
    </div>

    <div v-else-if="!task.current" class="text-body-2 text-medium-emphasis">
      {{ $t("workshop.checklist.no_result") }}
    </div>

    <template v-else>
      <!-- Slice 9: "Correggi" on each correctable value (Manager/Admin, the view's canCorrect;
           the API checks again) and "Valore precedente" under a corrected one. -->
      <div v-for="line in currentLines" :key="line.key" class="mb-1">
        <div class="d-flex align-start ga-2 text-body-2 workshop-wrap">
          <span class="mr-auto">
            <span class="text-medium-emphasis">{{ $t(line.key) }}:</span>
            {{ line.value }}
          </span>
          <v-btn
            v-if="line.correctable"
            size="x-small"
            variant="tonal"
            color="primary"
            class="workshop-correct"
            @click="$emit('correct', { task, field: line.field })"
          >
            {{ $t("acceptance.corrections.correct") }}
          </v-btn>
        </div>
        <div
          v-if="line.previous"
          class="text-caption text-medium-emphasis workshop-wrap"
        >
          {{
            $t("workshop.correction.previous", {
              value: line.previous.value,
              name: line.previous.name,
              date: line.previous.date,
            })
          }}
        </div>
      </div>

      <!-- Evidence of the current result, loaded as private blobs (revoked by the page). -->
      <div v-if="evidence.length" class="workshop-thumbs mt-2">
        <div
          v-for="item in evidence"
          :key="item.id"
          class="workshop-thumb"
          role="button"
          tabindex="0"
          :title="item.name"
          @click="openEvidence(item, evidence, 'current')"
          @keydown.enter="openEvidence(item, evidence, 'current')"
        >
          <template v-if="item.image">
            <img
              v-if="thumbs[item.id]"
              :src="thumbs[item.id]"
              :alt="item.name"
            />
            <v-icon v-else-if="failed[item.id]" color="grey">
              mdi-image-broken-variant
            </v-icon>
            <v-progress-circular v-else indeterminate size="24" color="grey" />
          </template>
          <v-icon v-else color="grey">mdi-file-pdf-box</v-icon>
          <span class="workshop-thumb-name text-caption">{{ item.name }}</span>
        </div>
      </div>
      <div
        v-if="openError && openErrorAt === 'current'"
        class="text-caption text-error mt-1"
      >
        {{ openError }}
      </div>

      <template v-if="task.current.result === 'exception'">
        <v-alert
          v-if="task.current.acceptance"
          type="success"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          <span class="workshop-wrap">
            {{
              $t("workshop.checklist.exception_accepted", {
                name: actorName(task.current.acceptance.acceptedBy),
                date: formatTime(task.current.acceptance.acceptedAt),
              })
            }}<template v-if="task.current.acceptance.note">
              · «{{ task.current.acceptance.note }}»</template
            >
          </span>
        </v-alert>
        <v-alert
          v-else
          type="warning"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          <v-chip size="small" variant="flat" color="amber-darken-2">
            {{ $t("workshop.checklist.exception_pending") }}
          </v-chip>
          <div class="text-body-2 mt-1">
            {{ $t("workshop.checklist.exception_note") }}
          </div>
          <!-- Manager/Admin (the view's canAcceptExceptions; the API checks again). -->
          <v-btn
            v-if="canAccept"
            color="green"
            variant="flat"
            prepend-icon="mdi-check-decagram-outline"
            class="mt-2 workshop-save"
            @click="$emit('accept', task)"
          >
            {{ $t("workshop.checklist.accept_exception") }}
          </v-btn>
        </v-alert>
      </template>

      <!-- Redo (slice 7): a new result replaces this one, which moves to the earlier results. -->
      <v-btn
        v-if="editable"
        variant="outlined"
        color="primary"
        prepend-icon="mdi-restore"
        class="mt-3 workshop-save"
        @click="startRedo"
      >
        {{ $t("workshop.checklist.redo") }}
      </v-btn>
    </template>

    <!-- Desktop: the webcam; phones use the native camera input above. -->
    <workshop-camera-dialog v-model="cameraOpen" @capture="addFile" />

    <file-preview-dialog
      v-model="preview.open"
      :title="$t(taskKey(task.code))"
      :file-ids="preview.fileIds"
      :start-id="preview.startId"
      :files="files"
      :blobs="blobs"
    />

    <!-- Earlier results, newest first, read-only; their thumbnails load when opened. -->
    <v-expansion-panels
      v-if="task.earlier.length"
      v-model="earlierPanel"
      class="mt-4"
    >
      <v-expansion-panel value="earlier">
        <v-expansion-panel-title>
          {{ $t("workshop.checklist.earlier", { count: task.earlier.length }) }}
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <div
            v-for="(result, index) in task.earlier"
            :key="result.id"
            :class="{ 'mt-3 pt-3 workshop-earlier': index > 0 }"
          >
            <div
              v-for="line in summary(result)"
              :key="line.key"
              class="text-body-2 workshop-wrap mb-1"
            >
              <span class="text-medium-emphasis">{{ $t(line.key) }}:</span>
              {{ line.value }}
            </div>
            <div
              v-if="earlierEvidence[result.id].length"
              class="workshop-thumbs mt-2"
            >
              <div
                v-for="item in earlierEvidence[result.id]"
                :key="item.id"
                class="workshop-thumb"
                role="button"
                tabindex="0"
                :title="item.name"
                @click="
                  openEvidence(item, earlierEvidence[result.id], result.id)
                "
                @keydown.enter="
                  openEvidence(item, earlierEvidence[result.id], result.id)
                "
              >
                <template v-if="item.image">
                  <img
                    v-if="thumbs[item.id]"
                    :src="thumbs[item.id]"
                    :alt="item.name"
                  />
                  <v-icon v-else-if="failed[item.id]" color="grey">
                    mdi-image-broken-variant
                  </v-icon>
                  <v-progress-circular
                    v-else
                    indeterminate
                    size="24"
                    color="grey"
                  />
                </template>
                <v-icon v-else color="grey">mdi-file-pdf-box</v-icon>
                <span class="workshop-thumb-name text-caption">
                  {{ item.name }}
                </span>
              </div>
            </div>
            <div
              v-if="openError && openErrorAt === result.id"
              class="text-caption text-error mt-1"
            >
              {{ openError }}
            </div>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-card>
</template>

<script>
import moment from "moment";
import FilePreviewDialog from "@/components/Acceptance/filePreviewDialog.vue";
import { FILE_TYPES } from "@/components/Acceptance/dossierFiles.mjs";
import WorkshopCameraDialog from "@/components/Workshop/workshopCameraDialog.vue";
import ContainerPicker from "@/components/Waste/containerPicker.vue";
import {
  groupKey,
  taskHint,
  taskKey,
  taskStatus,
} from "@/components/Workshop/workshopView.mjs";
import {
  CONTAINER_MAX,
  REASON_MAX,
  RESULT_BUTTONS,
  WORKSHOP_BASES,
  emptyResultForm,
  evidenceRequired,
  filesProblem,
  performedAtLimits,
  quickReasonKeys,
  resultFormErrors,
  resultPayload,
  visibleFields,
} from "@/components/Workshop/workshopForm.mjs";
import {
  correctableFields,
  displayText,
  fieldLabelKey,
  previousLines,
} from "@/components/Workshop/workshopCorrections.mjs";

// One task of the workshop checklist (Stage 3a plan §D2): group, position over all tasks (DF10),
// label, mandatory and status chips, the order hint with "Vai", the result form (slice 5) with
// evidence files (slice 6) while the task has no current result, the current result summary with
// its evidence thumbnails, the exception state, "Ripeti" (slice 7: the same form as a redo of the
// current result) and the earlier results with their thumbnails. The page saves (emit "save" with
// the request fields) and passes back the saving state and a failed save's error. A pending
// exception offers "Accetta eccezione" to a Manager/Admin (slice 8, emit "accept"; the page asks
// for confirmation). Read-only (editable false) once approved: no form, no Ripeti, no files.
// Slice 9: a Manager/Admin corrects each recorded value ("Correggi", emit "correct" with
// { task, field }; the page opens the correction dialog), before and after approval.
export default {
  name: "WorkshopTaskCard",
  components: {
    "file-preview-dialog": FilePreviewDialog,
    "workshop-camera-dialog": WorkshopCameraDialog,
    "container-picker": ContainerPicker,
  },
  props: {
    // One entry of the checklist view's tasks
    task: { type: Object, required: true },
    // All tasks of the checklist (the "n di 15")
    taskCount: { type: Number, required: true },
    // The view's progress.currentTaskCode
    currentTaskCode: { type: String, default: null },
    // False once the checklist is approved: no form.
    editable: { type: Boolean, default: false },
    // The dossier's arrival date (YYYY-MM-DD): the earliest performed-at day
    arrivalDate: { type: String, default: null },
    saving: { type: Boolean, default: false },
    // A failed save of this task: { field, code, text } (workshopForm.resultServerError)
    serverError: { type: Object, default: null },
    // view.files: the checklist's evidence metadata (mimetype, originalName) by id
    files: { type: Array, default: () => [] },
    // createBlobCache() from the page, which revokes the URLs on leave
    blobs: { type: Object, required: true },
    // "Accetta eccezione" on a pending exception (slice 8): the view's canAcceptExceptions
    canAccept: { type: Boolean, default: false },
    // "Correggi" on the current result's values (slice 9): the view's canCorrect. Also once
    // approved (corrections stay allowed).
    canCorrect: { type: Boolean, default: false },
    // Stage 6: waste on, fluids and batteries pick a waste container (store wasteEnabled).
    wasteEnabled: { type: Boolean, default: false },
  },
  emits: ["select", "save", "accept", "correct"],
  data() {
    return {
      form: emptyResultForm(this.task, new Date(), {
        wasteEnabled: this.wasteEnabled,
      }),
      // The picked container's pick-list row (waste on), for the unit check.
      pickedContainer: null,
      // Local previews of form.files, same order: { key, name, url (images only) }.
      previews: [],
      // Client errors show only after a first "Salva".
      attempted: false,
      resultButtons: RESULT_BUTTONS,
      reasonMax: REASON_MAX,
      containerMax: CONTAINER_MAX,
      fileAccept: FILE_TYPES.join(","),
      thumbs: {},
      failed: {},
      openError: "",
      // Where openError shows: "current" or an earlier result's id
      openErrorAt: null,
      // The current result's id while "Ripeti" is open (sent as replacesResultId), else null
      redoOf: null,
      // "earlier" while the earlier results are expanded
      earlierPanel: null,
      preview: { open: false, fileIds: [], startId: null },
      // The desktop webcam dialog
      cameraOpen: false,
    };
  },
  computed: {
    status() {
      return taskStatus(this.task, this.currentTaskCode);
    },
    // Only useful before the task is recorded.
    hint() {
      return this.task.current ? null : taskHint(this.task);
    },
    showForm() {
      return this.editable && (!this.task.current || Boolean(this.redoOf));
    },
    fields() {
      return visibleFields(this.task, this.form.result, {
        wasteEnabled: this.wasteEnabled,
      });
    },
    quickReasons() {
      return quickReasonKeys(this.task, this.form.result);
    },
    basisItems() {
      return WORKSHOP_BASES.map((basis) => ({
        value: basis,
        title: this.$t(`workshop.basis.${basis}`),
      }));
    },
    limits() {
      return performedAtLimits({ arrivalDate: this.arrivalDate });
    },
    // Recomputed on every edit, with the clock read again for the max.
    clientErrors() {
      if (!this.attempted) return {};
      const limits = performedAtLimits({ arrivalDate: this.arrivalDate });
      return resultFormErrors(this.form, this.task, limits, this.wasteOptions);
    },
    wasteOptions() {
      return {
        wasteEnabled: this.wasteEnabled,
        container: this.pickedContainer,
      };
    },
    evidenceNeeded() {
      return evidenceRequired(this.task, this.form.result);
    },
    // Over 3 files, 15 MB in total or a file the API refuses: "Salva" stays disabled.
    filesBroken() {
      return Boolean(filesProblem(this.form.files));
    },
    // The current result's summary lines with "Correggi" and "Valore precedente" (slice 9). A
    // Manager also sees the correctable fields without a value ("—"), so a note or a container
    // can be added.
    currentLines() {
      const result = this.task.current;
      if (!result) return [];
      const correctable = this.canCorrect
        ? correctableFields(this.task, result, {
            wasteEnabled: this.wasteEnabled,
          })
        : [];
      const previous = Object.fromEntries(
        previousLines(result, { locale: this.$i18n.locale }).map((item) => [
          item.field,
          {
            value: displayText(item.display, (key) => this.$t(key)),
            name: item.name,
            date: item.date,
          },
        ]),
      );
      return this.summary(result, { empty: correctable }).map((line) => ({
        ...line,
        correctable: Boolean(line.field) && correctable.includes(line.field),
        previous: (line.field && previous[line.field]) || null,
      }));
    },
    // The current result's evidence files: { id, name, image }.
    evidence() {
      return this.evidenceOf(this.task.current);
    },
    // The earlier results' evidence files by result id.
    earlierEvidence() {
      return Object.fromEntries(
        this.task.earlier.map((result) => [result.id, this.evidenceOf(result)]),
      );
    },
    // A server error not tied to a form field shows above "Salva".
    generalError() {
      const error = this.serverError;
      if (!error || error.field) return "";
      return error.code
        ? this.$t(`workshop.form.errors.${error.code}`)
        : error.text;
    },
  },
  watch: {
    // Another task: a fresh form.
    "task.code"() {
      this.resetForm();
      this.openError = "";
      this.earlierPanel = null;
    },
    // Another current result (saved, or someone else's after "Ricarica"): a fresh form and no
    // redo. A 409 changes nothing here, so the input stays under the conflict banner.
    "task.current"(current, old) {
      if ((current && current.id) !== (old && old.id)) this.resetForm();
    },
    evidence: {
      immediate: true,
      handler(items) {
        items
          .filter((item) => item.image)
          .forEach((item) => this.loadThumb(item.id));
      },
    },
    // Earlier thumbnails load only once the panel is open.
    earlierPanel: "loadEarlierThumbs",
    earlierEvidence: "loadEarlierThumbs",
  },
  beforeUnmount() {
    this.clearPreviews();
  },
  methods: {
    groupKey,
    taskKey,
    pickResult(result) {
      this.form.result = result;
    },
    // "Ripeti": the form opens empty as a redo of the current result.
    startRedo() {
      if (!this.task.current) return;
      this.resetForm();
      this.redoOf = this.task.current.id;
    },
    // A fresh form; also closes a redo ("Annulla").
    resetForm() {
      this.clearPreviews();
      this.form = emptyResultForm(this.task, new Date(), {
        wasteEnabled: this.wasteEnabled,
      });
      this.pickedContainer = null;
      this.attempted = false;
      this.redoOf = null;
    },
    // A result's evidence files: { id, name, image }.
    evidenceOf(result) {
      const ids = (result && result.evidenceFileIds) || [];
      return ids.map((id) => {
        const file = this.files.find((item) => item.id === id) || {};
        return {
          id,
          name: file.originalName || this.$t("workshop.checklist.evidence"),
          image: String(file.mimetype || "").startsWith("image/"),
        };
      });
    },
    loadEarlierThumbs() {
      if (this.earlierPanel !== "earlier") return;
      Object.values(this.earlierEvidence)
        .flat()
        .filter((item) => item.image)
        .forEach((item) => this.loadThumb(item.id));
    },
    // Files from the camera or the picker are added to the form, with a local preview.
    // "Scatta foto": the webcam dialog on a desktop (mouse pointer, camera API), the native
    // camera input on a phone or tablet, where `capture` opens the camera app.
    takePhoto() {
      const touch =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(pointer: coarse)").matches;
      const webcam = Boolean(
        navigator.mediaDevices && navigator.mediaDevices.getUserMedia,
      );
      if (!touch && webcam) this.cameraOpen = true;
      else this.$refs.camera.click();
    },
    addFiles(event) {
      const input = event.target;
      Array.from(input.files || []).forEach((file) => this.addFile(file));
      // The same file can be picked again after a removal.
      input.value = "";
    },
    addFile(file) {
      this.form.files.push(file);
      this.previews.push({
        key: `${file.name}:${file.size}:${file.lastModified}:${Math.random()}`,
        name: file.name,
        url: String(file.type).startsWith("image/")
          ? URL.createObjectURL(file)
          : null,
      });
    },
    removeFile(index) {
      const [removed] = this.previews.splice(index, 1);
      if (removed && removed.url) URL.revokeObjectURL(removed.url);
      this.form.files.splice(index, 1);
    },
    clearPreviews() {
      this.previews.forEach((item) => {
        if (item.url) URL.revokeObjectURL(item.url);
      });
      this.previews = [];
    },
    loadThumb(fileId) {
      if (this.thumbs[fileId]) return;
      this.blobs
        .get(fileId)
        .then((url) => {
          this.thumbs = { ...this.thumbs, [fileId]: url };
        })
        .catch((err) => {
          if (err && err.code === "revoked") return;
          this.failed = { ...this.failed, [fileId]: true };
        });
    },
    // Images open in the preview dialog with the same result's other images; PDFs in a new tab.
    // at is where a failure shows ("current" or the earlier result's id).
    async openEvidence(item, items, at) {
      this.openError = "";
      this.openErrorAt = at;
      if (item.image) {
        this.preview = {
          open: true,
          fileIds: items.filter((e) => e.image).map((e) => e.id),
          startId: item.id,
        };
        return;
      }
      // Opened before the download so the browser does not treat it as a popup.
      const tab = window.open("", "_blank");
      if (!tab) {
        this.openError = this.$t("acceptance.files.open_failed");
        return;
      }
      tab.opener = null;
      try {
        tab.location.href = await this.blobs.get(item.id);
      } catch (err) {
        tab.close();
        this.openError = this.$t("acceptance.files.open_failed");
      }
    },
    // The error under a field: the client check after a first "Salva", else the server's. The
    // upload limit shows at once.
    errorText(field) {
      if (field === "files") {
        const problem = filesProblem(this.form.files);
        if (problem) return this.$t(`workshop.form.errors.${problem}`);
      }
      const code = this.clientErrors[field];
      if (code) return this.$t(`workshop.form.errors.${code}`);
      const error = this.serverError;
      if (error && error.field === field) {
        return error.code
          ? this.$t(`workshop.form.errors.${error.code}`)
          : error.text;
      }
      return "";
    },
    save() {
      if (this.saving) return;
      this.attempted = true;
      // The max moves with the clock.
      const limits = performedAtLimits({ arrivalDate: this.arrivalDate });
      const errors = resultFormErrors(
        this.form,
        this.task,
        limits,
        this.wasteOptions,
      );
      if (Object.keys(errors).length) {
        return;
      }
      this.$emit(
        "save",
        resultPayload(this.form, this.task, {
          replacesResultId: this.redoOf,
          wasteEnabled: this.wasteEnabled,
        }),
      );
    },
    formatTime(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "";
    },
    actorName(actor) {
      return (actor && actor.name) || "—";
    },
    // [{ key, value, field }] of one result, only the fields it has; field names a correctable
    // value (slice 9). empty: correctable fields shown as "—" when the result has no value.
    summary(result, { empty = [] } = {}) {
      const lines = [
        {
          key: "workshop.checklist.result",
          value: this.$t(`workshop.result.${result.result}`),
        },
      ];
      if (result.quantity) {
        const value = new Intl.NumberFormat(this.$i18n.locale, {
          maximumFractionDigits: 3,
        }).format(result.quantity.value);
        const basis = result.quantity.basis
          ? ` · ${this.$t(`workshop.basis.${result.quantity.basis}`)}`
          : "";
        lines.push({
          key: "workshop.checklist.quantity",
          value: `${value} ${result.quantity.unit || ""}${basis}`,
          field: "quantity",
        });
      } else if (empty.includes("quantity")) {
        lines.push({
          key: "workshop.checklist.quantity",
          value: "—",
          field: "quantity",
        });
      }
      if (result.container || empty.includes("container")) {
        lines.push({
          key: "workshop.checklist.container",
          value: result.container || "—",
          field: "container",
        });
      }
      // A completed result's text is an optional note, not a reason.
      if (result.reason || empty.includes("reason")) {
        lines.push({
          key: fieldLabelKey("reason", result),
          value: result.reason || "—",
          field: "reason",
        });
      }
      lines.push({
        key: "workshop.checklist.performed_by",
        value: this.actorName(result.performedBy),
      });
      lines.push({
        key: "workshop.checklist.performed_at",
        value: this.formatTime(result.performedAt),
        field: "performedAt",
      });
      const evidence = (result.evidenceFileIds || []).length;
      if (evidence) {
        lines.push({ key: "workshop.checklist.evidence", value: evidence });
      }
      return lines;
    },
  },
};
</script>

<style scoped>
.workshop-wrap {
  overflow-wrap: anywhere;
}

.workshop-earlier {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

/* 2×2 result buttons, each at least 48 px high and wrapping its label. */
.workshop-result-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.workshop-result-btn {
  min-height: 48px;
  height: auto !important;
  padding-top: 8px;
  padding-bottom: 8px;
}

.workshop-quantity {
  flex: 1 1 140px;
  min-width: 0;
}

.workshop-basis {
  flex: 1 1 160px;
  min-width: 0;
}

.workshop-unit {
  height: 48px;
}

.workshop-save {
  min-height: 48px;
}

.workshop-file-btn {
  min-height: 48px;
}

/* Evidence thumbnails: 80 px boxes that wrap, the name under the image. */
.workshop-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.workshop-thumb {
  position: relative;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
}

.workshop-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.workshop-thumb-name {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0 4px;
  background: rgba(255, 255, 255, 0.85);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.workshop-thumb-remove {
  position: absolute;
  top: 2px;
  right: 2px;
}
</style>
